import {showMessage} from 'react-native-flash-message';
import {Linking} from 'react-native';

import getRemoteConfigValue from 'utils/firebase/remoteConfig';
import {setDynamicLink} from './actions';
import {setToken} from 'store/meeting/actions/properties';
import {setMe} from 'store/communication/actions/stateActions';
import {translate} from 'i18n/context/I18nContext';
import {VerifyAndParseToken} from 'utils/communication/Token';
import {logEvent} from 'utils/firebase/analytics';
import {truncateFirebaseAnalyticParameterValue} from 'utils/firebase/utils';
import {
  SET_USER_PERMISSIONS,
  INVALID_TOKEN,
} from 'utils/firebase/analytics.config';
import {
  persistAvatarSelection,
  readAvatarSelection,
} from './avatarPersistence.web';

// ─── Avatar URL persistence (web only) ──────────────────────────────────────
//
// After the customizer SPA finishes Save & Continue → Continue as Guest, it
// posts `{ type: 'ava-twin:avatar-saved', avatar_id, glb_url, skin_tone }` to
// `window.top`. We catch that here, persist the avatar id + skin tone in the
// URL and per-tab fallback storage, and dispatch `setAvatarData` so
// AvatarPreview can react and jump to DeviceConfig.

// Read URL persistence first, then the per-tab fallback used by one short-lived
// release. The meeting token and unrelated parameters remain unchanged.
export const readAvatarParamsFromUrl = () => {
  return readAvatarSelection();
};

// Persist to both the URL and per-tab fallback storage.
export const writeAvatarParamsToUrl = (avatarId, skinTone) => {
  persistAvatarSelection(avatarId, skinTone);
};

// Install a postMessage listener that catches "ava-twin:avatar-saved" from
// the customizer iframe (which now posts to window.top thanks to the SDK
// customizer SPA fix), writes the params to the URL, and dispatches
// setAvatarData so the rest of the app can react. Also dispatches
// setCustomizerClosing(true) so a fullscreen <Loading> overlay covers the
// gap between the iframe closing and DeviceConfig rendering. Returns an
// unsubscribe function.
export const installAvatarSavedListener = (
  dispatch,
  setAvatarData,
  setCustomizerClosing,
) => {
  if (typeof window === 'undefined') return () => {};
  const onMessage = (event) => {
    const data = event.data;
    if (!data || typeof data !== 'object') return;
    if (data.type !== 'ava-twin:avatar-saved') return;
    const avatarId = data.avatar_id;
    const skinTone = data.skin_tone;
    if (!avatarId) return;
    console.log(
      '[WEBGL_DBG] linkActions: ava-twin:avatar-saved received avatarId=' +
        avatarId +
        ' skinTone=' +
        (skinTone || 'null'),
    );
    writeAvatarParamsToUrl(avatarId, skinTone);
    if (typeof setAvatarData === 'function') {
      dispatch(setAvatarData(avatarId, skinTone || null));
    }
    if (typeof setCustomizerClosing === 'function') {
      dispatch(setCustomizerClosing(true));
    }
  };
  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
};

export const initDynamicLinkHandler = () => {
  const marcusSiteUrl = getRemoteConfigValue('marcus_site_url');
  const articlePath = '/blog/issues-joining-a-3dmeet-meeting/';
  return async (dispatch, getState) => {
    console.log('[WEBGL_DBG] linkActions: reading token from URL');
    const token = new URLSearchParams(window.location.search).get('token');
    console.log('[WEBGL_DBG] linkActions: token present=' + (token ? 'yes (len=' + token.length + ')' : 'no'));

    // Verify token (reject/return to previous state if invalid)
    const verifiedToken = VerifyAndParseToken(token);
    const {error} = verifiedToken;
    console.log('[WEBGL_DBG] linkActions: token verify result=' + (!error ? 'OK' : 'FAILED err=' + (error.message || error)));
    if (error) {
      await dispatch(setDynamicLink(undefined));

      if (token) {
        showMessage({
          type: 'danger',
          message: translate('linkActionsWeb.clickForSupport'),
          autoHide: false,
          onPress: () => {
            Linking.openURL(`${marcusSiteUrl.asString()}${articlePath}`);
          },
        });

        await logEvent(INVALID_TOKEN, {
          error: error,
        });

        console.error(error);
      }
    } else {
      await dispatch(setDynamicLink(token));
      await dispatch(setToken(token));

      const {peerId, permissions, roomId, integration} = verifiedToken;
      const displayName = translate('linkActionsWeb.userNum', {
        num: peerId.substring(0, 4),
      });
      const displayNameSet = false;
      const device = {};
      await dispatch(
        setMe({peerId, displayName, displayNameSet, device, permissions}),
      );

      const permissionsString = permissions.join(' ');
      await logEvent(SET_USER_PERMISSIONS, {
        meeting_id: roomId,
        integration: integration,
        permissions: truncateFirebaseAnalyticParameterValue(permissionsString),
      });
    }
  };
};
